import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { RootState } from 'MyTypes';
import query from 'query-string';

import _ from '@portal/common/locale';
import { withGoogleAnalytics } from '@portal/common/services/google-analytics';
import { parseApiError } from '@portal/common/utility/api-helpers';

import api from '../../api';
import { FORGOT_PASSWORD_PATH, HOME_PATH, REGISTRATION_PATH } from '../../router/paths';
import { removeLocalStorageItem } from '../../services/localStorageService';
import { selectOrganization } from '../../store/organization/actions';
import { destroySession, resetSession, signIn } from '../../store/session/actions';
import { getSessionAccount } from '../../store/session/selectors';
import SignInSceneView from './SignInSceneView';

type SignInSceneProps = {
    trackEvent: (event: { category: string; action: string }) => void;
    successMessages?: string[];
};

const SignInScene: React.FC<SignInSceneProps> = ({
    trackEvent,
    successMessages: propSuccessMessages,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    const [errors, setErrors] = useState<object[] | null>(null);

    // Get success messages from location state if available
    const locationSuccessMessages = location.state?.successMessages;
    const successMessages = locationSuccessMessages || propSuccessMessages;

    // Parse 'back' parameter from URL query, defaulting to '/'
    const { back = '/' } = query.parse(location.search);

    // Redux selectors
    const sessionAccount = useSelector(getSessionAccount);
    const errorMessage = useSelector((state: RootState) => state.session.error);

    useEffect(() => {
        if (sessionAccount) {
            navigate(HOME_PATH);
        }
    }, [sessionAccount, navigate]);

    const clearErrors = () => setErrors(null);

    const setErrorsWithSession = (err: Error) => {
        dispatch(destroySession());
        setErrors(parseApiError(err));
    };

    const handleSignIn = (email: string, password: string) => dispatch(signIn(email, password));

    const handleForgotPassword = () => navigate(FORGOT_PASSWORD_PATH);
    const handleRegisterClick = () => navigate(REGISTRATION_PATH);

    const handleSuccess = async (session: any) => {
        clearErrors();
        trackEvent({ category: 'Account', action: 'Sign In' });

        // Clear entered cipher for Simulation Tool
        removeLocalStorageItem('cipher');

        const { account } = session;
        const { organization } = account;

        if (organization == null) {
            setErrorsWithSession(new Error(_('error_user_has_no_organization')));
            return;
        }

        if (!organization.is_enabled) {
            setErrorsWithSession(new Error(_('error_disabled_organization')));
            return;
        }

        dispatch(selectOrganization(organization));
        navigate(back as string, { replace: true });
    };

    const signInWithSSO = async () => {
        const url = await api.auth.getEndpoint();
        dispatch(resetSession());
        window.open(url, '_self');
    };

    const combinedErrors = errorMessage
        ? {
              'Session Error': errorMessage,
              ...errors,
          }
        : errors;

    return (
        <SignInSceneView
            signIn={handleSignIn}
            onSuccess={handleSuccess}
            onForgotPassword={handleForgotPassword}
            onRegisterClick={handleRegisterClick}
            onSignInWithSSO={signInWithSSO}
            errors={combinedErrors}
            successMessages={successMessages}
        />
    );
};

export default withGoogleAnalytics({
    trackMethods: () => ({
        handleSuccess: () => ({
            category: 'Account',
            action: 'Sign In',
        }),
    }),
})(SignInScene);
